import { PlainTooltip } from "@similarweb/ui-components";
import { translate } from "app/filters/i18nFilter";
import type { SignalTemplatesDropdownProps } from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalTemplatesDropdown/SignalTemplatesDropdown";
import SignalTemplatesDropdown from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalTemplatesDropdown/SignalTemplatesDropdown";
import useSignalFiltersViewResolver from "app/pages/sales-intelligence/sub-modules/signals/hooks/useSignalFiltersViewResolver";
import signalsService from "app/pages/sales-intelligence/sub-modules/signals/services/signalsService";
import type { ReactElement } from "react";
import { createElement, useMemo, useRef, useState } from "react";
import type SignalFilters from "../../../models/core/SignalFilters";
import type SignalCreateDTO from "../../../models/miscellaneous/SignalCreateDTO";
import type SignalTemplate from "../../../models/templates/SignalTemplate";
import type ModifySignalViewModel from "../../../models/view/signal-modal/ModifySignalViewModel";
import type { SubmitRendererProps } from "../../../models/view/SignalFiltersViewProps";
import FiltersViewSubmitButton from "../../buttons/FiltersViewSubmitButton/FiltersViewSubmitButton";

type SelectedTemplate<F extends SignalFilters> = Readonly<SignalTemplate<F>> | null;

type Props<Filters extends SignalFilters> = {
    isSubmitInProgress: boolean;
    viewModel: Readonly<ModifySignalViewModel>;
    onCreateSubmit(dto: Readonly<SignalCreateDTO<Filters>>): void;
};

const SignalsCreateView = <F extends SignalFilters>(props: Props<F>) => {
    const { isSubmitInProgress, viewModel: vm, onCreateSubmit } = props;

    const { current: groupedTemplates } = useRef<ReadonlyArray<SignalTemplate<F>[]>>(
        vm.getGroupedTemplatesOfType(vm.initiallySelectedType),
    );
    const { current: defaultTemplate } = useRef<Readonly<SignalTemplate<F>>>(
        vm.getDefaultTemplate(vm.initiallySelectedType),
    );
    const { current: initialTemplate } = useRef<Readonly<SignalTemplate<F>>>(
        vm.getInitialTemplate(vm.initiallySelectedType),
    );

    const [selectedTemplate, setSelectedTemplate] = useState<SelectedTemplate<F>>(initialTemplate);

    const selectedOrDefaultTemplate = selectedTemplate ?? defaultTemplate;

    const viewResolver = useSignalFiltersViewResolver();
    const FiltersView = useMemo(() => {
        return viewResolver.getFiltersViewWithValidation(vm.initiallySelectedType);
    }, [vm.initiallySelectedType, viewResolver]);

    function handleTemplateSelect(template: NonNullable<SelectedTemplate<F>>) {
        setSelectedTemplate(template);
    }

    function handleCreateSubmit(filters: Readonly<F>) {
        if (!selectedOrDefaultTemplate.isValid()) {
            console.error("SignalsCreateView: unable to create a signal from", selectedOrDefaultTemplate);

            return;
        }

        onCreateSubmit(signalsService.getSignalCreateDTO(selectedOrDefaultTemplate, filters, vm.signalSource));
    }

    function renderTemplateView({ onSelect, ...restProps }: Partial<SignalTemplatesDropdownProps> = {}): ReactElement {
        const handleSelect = (template: NonNullable<SelectedTemplate<F>>): void => {
            handleTemplateSelect(template);
            onSelect?.(template);
        };

        return (
            <SignalTemplatesDropdown
                defaultOpen={!initialTemplate}
                selectedTemplate={selectedTemplate}
                groupedTemplates={groupedTemplates}
                onSelect={handleSelect}
                {...restProps}
            />
        );
    }

    function renderSubmitView({ isSignalValid = false, isDisabled = false, onSubmit }: SubmitRendererProps) {
        const isSubmitDisabled = isDisabled || !selectedOrDefaultTemplate.isValid();

        return (
            <PlainTooltip
                enabled={isSignalValid ? isSubmitDisabled : null}
                tooltipContent={translate("si.signals.button.save-apply.disabled.tooltip")}
            >
                <div>
                    <FiltersViewSubmitButton
                        isDisabled={isSubmitDisabled || !isSignalValid}
                        isSubmitInProgress={isSubmitInProgress}
                        text={{ key: "si.signals.button.save-apply.text.short" }}
                        onClick={onSubmit}
                    />
                </div>
            </PlainTooltip>
        );
    }

    if (typeof FiltersView === "undefined") {
        // TODO: Possibly worth throwing an error and catching it in ErrorBoundary
        console.error("SignalsCreateView: unable to find a filter view");

        return null;
    }

    // FiltersView is resolved dynamically per signal type and wrapped by an HOC on every
    // resolution, so it can't be expressed as a static JSX tag; the useMemo above keeps it stable.
    return createElement(FiltersView, {
        isActive: selectedTemplate !== null,
        filters: selectedOrDefaultTemplate.filters,
        signalAlias: selectedOrDefaultTemplate.alias,
        signalSubType: selectedOrDefaultTemplate.signalSubType,
        signalTemplateHint: selectedOrDefaultTemplate.templateHint,
        renderTemplateView,
        renderSubmit: renderSubmitView,
        onFiltersSubmit: handleCreateSubmit,
    });
};

export default SignalsCreateView;
